/**
 * Dashboard data hook
 * Aggregates data from multiple API endpoints for the dashboard
 */
import { useQuery } from '@tanstack/react-query';
import { accountsService, type BalanceSummary } from '@/services/accounts';
import { transactionsService, type SpendingByCategory, type PeriodSummary } from '@/services/transactions';
import { connectionsApi } from '@/services/connections';
import { flowsService, type FlowSnapshot } from '@/services/flows';
import type { Transaction, Connection } from '@/types';
import { getRateLimitRetryMessage } from '@/utils/errorMessages';

// ============================================================================
// Types
// ============================================================================

export interface DashboardStats {
  totalBalance: number;
  incomeLast30Days: number;
  expensesLast30Days: number;
  netLast30Days: number;
  accountsCount: number;
  lastSync: string | null;
}

export interface DashboardData {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  stats: DashboardStats;
  recentTransactions: Transaction[];
  connections: Connection[];
  hasConnections: boolean;
  spendingByCategory: SpendingByCategory[];
  /** WP-DASH-2: Flow snapshot for mini Sankey visualization */
  flowSnapshot: FlowSnapshot | null;
  /** WP-CORE-9: the retry message when the flow was refused by the household's calculation limit (429) */
  flowRateLimitMessage: string | null;
  refetch: () => void;
}

// ============================================================================
// Query Keys
// ============================================================================

export const dashboardKeys = {
  all: ['dashboard'] as const,
  balances: () => [...dashboardKeys.all, 'balances'] as const,
  transactions: () => [...dashboardKeys.all, 'transactions'] as const,
  connections: () => [...dashboardKeys.all, 'connections'] as const,
  spending: () => [...dashboardKeys.all, 'spending'] as const,
  periodSummary: () => [...dashboardKeys.all, 'periodSummary'] as const,
  /** WP-DASH-2: Flow snapshot for mini Sankey visualization */
  flow: () => [...dashboardKeys.all, 'flow'] as const,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the start and end dates for the last 30 days (rolling window)
 * Provides consistent data visibility regardless of calendar date
 * WP-DASH-1: Per sankey-flows.md Section 5.1 - last_30_days is a pre-generated period
 */
function getLast30Days(): { startDate: string; endDate: string } {
  const now = new Date();
  // Set end date to today at midnight for consistent boundary
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Start date is 30 days ago
  const startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    startDate: startDate.toISOString().split('T')[0] ?? '',
    endDate: endDate.toISOString().split('T')[0] ?? '',
  };
}

// Default stats are computed by computeStats function with zero values when data unavailable

/**
 * Compute dashboard stats from API responses
 * Handles missing/null data gracefully
 */
function computeStats(
  balances: BalanceSummary | undefined | null,
  periodSummary: PeriodSummary | undefined | null,
  connections: Connection[] | undefined | null
): DashboardStats {
  // Get income and expenses from the period summary endpoint
  // This endpoint correctly calculates both income (amount > 0) and expenses (amount < 0)
  // WP-DASH-1: Now using rolling 30-day window instead of calendar month
  const incomeLast30Days = Number(periodSummary?.totalIncome) || 0;
  const expensesLast30Days = Number(periodSummary?.totalExpenses) || 0;
  const netLast30Days = Number(periodSummary?.net) || (incomeLast30Days - expensesLast30Days);

  // Find the most recent sync time across all connections
  let lastSync: string | null = null;
  if (connections && Array.isArray(connections) && connections.length > 0) {
    const syncTimes = connections
      .map((c) => c?.lastSyncAt)
      .filter((t): t is string => typeof t === 'string' && t !== null)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    lastSync = syncTimes[0] || null;
  }

  // Safely access balance data with fallbacks
  const netWorth = Number(balances?.netWorth) || 0;
  const byType = balances?.byType;
  const accountsCount = Array.isArray(byType)
    ? byType.reduce((acc, t) => acc + (Number(t?.count) || 0), 0)
    : 0;

  return {
    totalBalance: netWorth,
    incomeLast30Days,
    expensesLast30Days,
    netLast30Days,
    accountsCount,
    lastSync,
  };
}

// ============================================================================
// Hook
// ============================================================================

export function useDashboardData(): DashboardData {
  const { startDate, endDate } = getLast30Days();

  // Fetch balance summary
  const balancesQuery = useQuery({
    queryKey: dashboardKeys.balances(),
    queryFn: () => accountsService.getBalanceSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch recent transactions
  const transactionsQuery = useQuery({
    queryKey: dashboardKeys.transactions(),
    queryFn: () =>
      transactionsService.getAll({
        page: 1,
        pageSize: 5,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch connections
  const connectionsQuery = useQuery({
    queryKey: dashboardKeys.connections(),
    queryFn: () => connectionsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch spending by category for current month
  const spendingQuery = useQuery({
    queryKey: dashboardKeys.spending(),
    queryFn: () =>
      transactionsService.getSpendingByCategory({
        startDate,
        endDate,
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch period summary (income and expenses) for current month
  const periodSummaryQuery = useQuery({
    queryKey: dashboardKeys.periodSummary(),
    queryFn: () =>
      transactionsService.getPeriodSummary({
        startDate,
        endDate,
      }),
    staleTime: 5 * 60 * 1000,
  });

  // Determine if user has connections before fetching flow data
  const hasConnectionsValue = Array.isArray(connectionsQuery.data) && connectionsQuery.data.length > 0;

  // WP-DASH-2: Fetch flow snapshot for mini Sankey visualization
  // Deferred until connections are loaded to avoid unnecessary API calls
  const flowQuery = useQuery({
    queryKey: dashboardKeys.flow(),
    queryFn: () => flowsService.getLast30Days(),
    staleTime: 5 * 60 * 1000,
    // Only fetch if user has connections (no point otherwise)
    enabled: hasConnectionsValue && !connectionsQuery.isLoading,
  });

  const isLoading =
    balancesQuery.isLoading ||
    transactionsQuery.isLoading ||
    connectionsQuery.isLoading ||
    spendingQuery.isLoading ||
    periodSummaryQuery.isLoading;

  // Only show error for critical failures (auth errors or when ALL queries fail)
  // Individual query failures are handled gracefully with fallback data
  const allQueriesSettled = !isLoading;
  const criticalQueries = [balancesQuery, connectionsQuery]; // Core data
  const allCriticalFailed = criticalQueries.every((q) => q.isError);

  // Check if any error is an auth error (401/403) which should always be shown
  const hasAuthError = [balancesQuery, transactionsQuery, connectionsQuery, spendingQuery, periodSummaryQuery]
    .some((q) => {
      const err = q.error;
      if (err && typeof err === 'object' && 'status' in err) {
        const status = (err as { status: number }).status;
        return status === 401 || status === 403;
      }
      return false;
    });

  // Show error only for auth issues or when critical queries all fail
  const isError = allQueriesSettled && (hasAuthError || allCriticalFailed);

  // Get the first error for display purposes (prefer auth errors)
  const error: Error | null = hasAuthError
    ? ([balancesQuery, transactionsQuery, connectionsQuery, spendingQuery, periodSummaryQuery]
        .find((q) => {
          const err = q.error;
          if (err && typeof err === 'object' && 'status' in err) {
            const status = (err as { status: number }).status;
            return status === 401 || status === 403;
          }
          return false;
        })?.error || null)
    : (allCriticalFailed
        ? (balancesQuery.error || connectionsQuery.error || null)
        : null);

  // Safely extract connections array
  const connections: Connection[] = Array.isArray(connectionsQuery.data)
    ? connectionsQuery.data
    : [];

  // Safely extract spending data
  const spendingData = Array.isArray(spendingQuery.data) ? spendingQuery.data : [];

  // Compute stats with graceful fallbacks using the period summary
  const stats = computeStats(balancesQuery.data, periodSummaryQuery.data, connections);

  // Safely extract transactions array
  const transactionsData = transactionsQuery.data;
  const recentTransactions: Transaction[] =
    transactionsData && Array.isArray(transactionsData.transactions)
      ? transactionsData.transactions
      : [];

  const spendingByCategory = spendingData;

  const refetch = () => {
    balancesQuery.refetch();
    transactionsQuery.refetch();
    connectionsQuery.refetch();
    spendingQuery.refetch();
    periodSummaryQuery.refetch();
    // Only refetch flow if we have connections
    if (hasConnectionsValue) {
      flowQuery.refetch();
    }
  };

  // WP-DASH-2: Extract flow snapshot with null fallback
  const flowSnapshot: FlowSnapshot | null = flowQuery.data ?? null;
  const flowRateLimitMessage = getRateLimitRetryMessage(flowQuery.error);

  return {
    isLoading,
    isError,
    error,
    stats,
    recentTransactions,
    connections,
    hasConnections: connections.length > 0,
    spendingByCategory,
    flowSnapshot,
    flowRateLimitMessage,
    refetch,
  };
}

export default useDashboardData;
